import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Car, Bike, Info } from "lucide-react";
import { useLang, ASSETS } from "../i18n";
import { autoFormulas, motoFormula } from "../data";
import { PriceMatrix } from "./PriceMatrix";

const Tab = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`relative px-6 py-3 text-sm font-semibold rounded-full transition-colors ${
      active ? "text-black" : "text-neutral-300 hover:text-white"
    }`}
  >
    {active && (
      <motion.span
        layoutId="pillBg"
        className="absolute inset-0 bg-[var(--c-gold)] rounded-full"
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
      />
    )}
    <span className="relative flex items-center gap-2">{children}</span>
  </button>
);

const SubTab = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-5 py-2 text-xs font-mono uppercase tracking-widest rounded-full border transition-colors ${
      active
        ? "border-[var(--c-gold)] text-[var(--c-gold)] bg-[var(--c-gold)]/10"
        : "border-white/10 text-neutral-400 hover:text-white"
    }`}
  >
    {children}
  </button>
);

const ComboCards = () => {
  const { t } = useLang();
  const onBook = () => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  return (
    <div className="mt-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h4 className="font-display text-3xl text-white">{t.pricing.comboTitle}</h4>
          <p className="text-sm text-neutral-400 mt-1">{t.pricing.comboSub}</p>
        </div>
        <span className="self-start text-xs font-mono uppercase tracking-widest px-3 py-1.5 rounded-full bg-[var(--c-red)]/20 text-[var(--c-red-bright)] border border-[var(--c-red)]/40">
          {t.pricing.comboDiscount}
        </span>
      </div>
      <div className="grid sm:grid-cols-3 gap-5">
        {autoFormulas.combo.map((c, i) => (
          <div
            key={c.id}
            data-testid={`combo-card-${c.id}`}
            className={`hover-lift rounded-2xl p-7 border ${
              i === 1
                ? "bg-gradient-to-b from-[var(--c-gold)]/15 to-[var(--c-bg-3)] border-[var(--c-gold)]/60 shadow-[0_24px_60px_-24px_rgba(212,175,55,0.4)]"
                : "bg-[var(--c-bg-3)] border-[var(--c-gold)]/25 shadow-[0_18px_50px_-30px_rgba(0,0,0,0.9)]"
            }`}
          >
            <p className="font-display text-2xl text-white">{c.name}</p>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="gold-text font-semibold text-4xl font-display">{c.withDiscount}</span>
              <span className="text-xs text-neutral-500">{t.pricing.currency}</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">
              {t.pricing.from} · {t.pricing.withDiscount}
            </p>
            <p className="text-sm text-neutral-600 line-through mt-3">
              {c.withoutDiscount} {t.pricing.currency} · {t.pricing.withoutDiscount}
            </p>
            <button
              data-testid={`combo-book-${c.id}`}
              onClick={onBook}
              className="mt-6 w-full text-xs font-semibold uppercase tracking-wider py-2.5 rounded-full gold-border text-[var(--c-gold)] hover:bg-white/5 transition-colors"
            >
              {t.pricing.book}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export const Pricing = () => {
  const { t } = useLang();
  const [vehicle, setVehicle] = useState("auto");
  const [autoPart, setAutoPart] = useState("interior");

  return (
    <section id="pricing" className="relative py-28 md:py-36 bg-[var(--c-bg)] grain overflow-hidden">
      <img
        src={ASSETS.ducati}
        alt=""
        className="pointer-events-none absolute -left-40 bottom-0 w-[55%] opacity-[0.06] hidden lg:block"
      />
      <div className="relative max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-[var(--c-gold)]" />
            <span className="overline">{t.pricing.tag}</span>
            <span className="h-px w-10 bg-[var(--c-gold)]" />
          </div>
          <h2 className="font-display text-5xl sm:text-6xl font-semibold text-white">{t.pricing.title}</h2>
          <p className="mt-5 text-neutral-400 max-w-2xl mx-auto">{t.pricing.sub}</p>
        </div>

        {/* vehicle toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex gap-1 p-1.5 rounded-full bg-[var(--c-bg-3)] gold-border">
            <Tab active={vehicle === "auto"} onClick={() => setVehicle("auto")} testid="pricing-tab-auto">
              <Car size={16} /> {t.pricing.auto}
            </Tab>
            <Tab active={vehicle === "moto"} onClick={() => setVehicle("moto")} testid="pricing-tab-moto">
              <Bike size={16} /> {t.pricing.moto}
            </Tab>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {vehicle === "auto" ? (
            <motion.div
              key="auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
            >
              <div className="flex justify-center gap-3 mb-8">
                <SubTab active={autoPart === "interior"} onClick={() => setAutoPart("interior")} testid="auto-subtab-interior">
                  {t.pricing.interior}
                </SubTab>
                <SubTab active={autoPart === "exterior"} onClick={() => setAutoPart("exterior")} testid="auto-subtab-exterior">
                  {t.pricing.exterior}
                </SubTab>
              </div>
              <div className="rounded-3xl border border-[var(--c-gold)]/30 bg-gradient-to-b from-[var(--c-bg-3)]/80 to-[var(--c-bg)] p-4 md:p-7 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)]">
                <PriceMatrix
                  tiers={autoFormulas[autoPart].tiers}
                  rows={autoFormulas[autoPart].rows}
                  highlight={1}
                />
              </div>
              <p className="flex items-center gap-2 mt-5 text-xs text-neutral-500">
                <Info size={14} className="text-[var(--c-gold)]" /> {t.pricing.autoNote}
              </p>
              <ComboCards />
            </motion.div>
          ) : (
            <motion.div
              key="moto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4 }}
            >
              <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-neutral-500 mb-7">
                {t.pricing.motoTypes}
              </p>
              <div className="rounded-3xl border border-[var(--c-gold)]/30 bg-gradient-to-b from-[var(--c-bg-3)]/80 to-[var(--c-bg)] p-4 md:p-7 shadow-[0_40px_100px_-40px_rgba(0,0,0,0.9)]">
                <PriceMatrix tiers={motoFormula.tiers} rows={motoFormula.rows} highlight={2} />
              </div>
              <p className="flex items-center gap-2 mt-5 text-xs text-neutral-500">
                <Info size={14} className="text-[var(--c-gold)]" /> {t.pricing.motoNote}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
